import { Outlet } from "@tanstack/react-router"
import { buildCampaignFormDefaults, useCampaignForm } from "modules/campaigns/hooks/useCampaignForm"
import { useCampaignQuery } from "modules/campaigns/hooks/useCampaignQuery"
import { CampaignSaveProvider, useCampaignSave } from "modules/campaigns/hooks/useCampaignSave"
import { HeaderButtons } from "modules/shared/components/Breadcrumbs/HeaderButtons"
import { Loading } from "modules/shared/components/Loading"
import { useEffect, useMemo } from "react"
import { FormProvider } from "react-hook-form"
import { CampaignDetailResponseDataSchema } from "schema"
import { z } from "zod"
import { ArchivedCampaignBanner } from "./ArchivedCampaignBanner"
import { CampaignHeader } from "./CampaignHeader"
import { CampaignHeaderActions } from "./CampaignHeaderActions"
import { CampaignTabs } from "./CampaignTabs"
import { CampaignTimeFrameProvider } from "./CampaignTimeFrameContext"
import { CampaignViewFormatProvider } from "./CampaignViewFormatContext"
import { PendingImports } from "./PendingImports"
import { ScheduledLaunches } from "./ScheduledLaunches"
import { TestCustomer } from "./TestCustomer"

type Props = {
  id: string
}

export function CampaignDetail({ id }: Props) {
  const { data: campaign, isPending } = useCampaignQuery(id)

  if (isPending || !campaign) return <Loading />

  return <CampaignDetailContent id={id} campaign={campaign} />
}

type ContentProps = {
  id: string
  campaign: z.infer<typeof CampaignDetailResponseDataSchema>
}

function CampaignDetailContent({ id, campaign }: ContentProps) {
  const form = useCampaignForm(campaign)
  const {
    flush,
    syncFromCampaign,
    issuesByField,
    savingFields,
    setValidationReviewOpen,
    setComposerPromptEditing,
  } = useCampaignSave(form, campaign.id)

  const defaultsFromCampaign = useMemo(() => buildCampaignFormDefaults(campaign), [campaign])

  useEffect(() => {
    syncFromCampaign(defaultsFromCampaign)
  }, [defaultsFromCampaign, syncFromCampaign])

  const saveCtx = useMemo(
    () => ({
      flush,
      issuesByField,
      savingFields,
      setValidationReviewOpen,
      setComposerPromptEditing,
    }),
    [flush, issuesByField, savingFields, setValidationReviewOpen, setComposerPromptEditing],
  )

  return (
    <FormProvider {...form}>
      <CampaignSaveProvider value={saveCtx}>
        {!campaign.isLogistics && (
          <HeaderButtons>
            <CampaignHeaderActions campaign={campaign} />
            <TestCustomer campaignId={id} />
          </HeaderButtons>
        )}
        <div className="flex flex-col h-full">
          <ArchivedCampaignBanner campaign={campaign} />
          <div className="flex flex-col flex-1 min-h-0 gap-6 p-6 relative">
            <CampaignHeader campaign={campaign} />
            <div className="flex flex-col gap-4 empty:hidden">
              <ScheduledLaunches campaign={campaign} />
              <PendingImports campaign={campaign} />
            </div>
            <div className="flex flex-col flex-1 min-h-0 w-full gap-5">
              <CampaignTimeFrameProvider>
                <CampaignViewFormatProvider>
                  <CampaignTabs id={id} isLogistics={campaign.isLogistics} />
                  <Outlet />
                </CampaignViewFormatProvider>
              </CampaignTimeFrameProvider>
            </div>
          </div>
        </div>
      </CampaignSaveProvider>
    </FormProvider>
  )
}
